import { useState, useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { cloneDeep } from "lodash";
import { Dialog } from "primereact/dialog";
import { Toast } from "primereact/toast";
import { GMap } from "primereact/gmap";
import { ButtonInput } from "../../components/ButtonInput/ButtonInput";
import { TextInputWithIcon } from "../../components/TextInputWithIcon/TextInputWithIcon";
import { RadioButtonInput } from "../../components/RadioButtonInput/RadioButtonInput";
import { DropdownInputWithIcon } from "../../components/DropdownInputWithIcon/DropdownInputWithIcon";
import { Warehouse, WarehouseStatus } from "../../models/Warehouse";
import { City } from "../../models/City";
import { RoleType } from "../../models/Role";
import { activeUser } from "../../services/AuthService";
import { loadGoogleMaps, removeGoogleMaps } from "./googlemaps";
import {
  getAllWarehouses,
  createWarehouse,
  updateOneWarehouse,
} from "../../services/WarehouseService";

import {
  getAllCities,
  createCity,
  updateOneCity,
} from "../../services/CityService";
import "./NewWarehouseForm.scss";

export interface NewWarehouseFormProps {
  show?: string;
  warehouse?: Warehouse;
  buttonLabel?: string;
  buttonIcon?: string;
  buttonStyle?: React.CSSProperties;
  buttonSaveStyle?: React.CSSProperties;
  buttonNewCityStyle?: React.CSSProperties;
  buttonInactiveStyle?: React.CSSProperties;
  refreshingData?: () => void;
}

export const NewWarehouseForm = (p: NewWarehouseFormProps) => {
  const {
    warehouse,
    show,
    buttonLabel,
    buttonIcon,
    buttonStyle,
    buttonSaveStyle,
    buttonNewCityStyle,
    buttonInactiveStyle,
    refreshingData,
  } = p;

  const { t } = useTranslation();
  const toast = useRef<Toast>(null);

  const [visibleNewWarehouseForm, setVisibleNewWarehouseForm] =
    useState<boolean>(false);
  const [newWarehouse, setNewWarehouse] = useState<Warehouse>({
    id: warehouse?.id ?? "",
    code: warehouse?.code ?? "",
    name: warehouse?.name ?? "",
    address: warehouse?.address ?? "",
    city: warehouse?.city ?? ({} as City),
    latitude: warehouse?.latitude ?? undefined,
    longitude: warehouse?.longitude ?? undefined,
    type: warehouse?.type ?? "",
    amount: warehouse?.amount ?? undefined,
    weighbridgeModel: warehouse?.weighbridgeModel ?? "",
    weighbridgePort: warehouse?.weighbridgePort ?? "",
    automated: warehouse?.automated ?? undefined,
    calculateCalo: warehouse?.calculateCalo ?? undefined,
    status: warehouse?.status ?? WarehouseStatus.Open,
    virtual: warehouse?.virtual ?? false,
    finishingCenter: warehouse?.finishingCenter ?? false,
  });
  const [visibleNewCityAddForm, setVisibleNewCityAddForm] =
    useState<boolean>(false);
  const [citiesApi, setCitiesApi] = useState<City[]>([]);
  const [warehousesApi, setWarehousesApi] = useState<Warehouse[]>([]);

  const [newCity, setNewCity] = useState<City>({
    name: "",
    code: "",
    id: "",
  });
  const [googleMapsReady, setGoogleMapsReady] = useState<boolean>(false);
  const [dialogMapsVisible, setDialogMapsVisible] = useState<boolean>(false);
  const [selectedPosition, setSelectedPosition] = useState(null);
  const [overlays, setOverlays] = useState<any[]>([]);
  const infoWindow = useRef<any>(null);

  useEffect(() => {
    loadGoogleMaps(() => {
      setGoogleMapsReady(true);
    });

    return () => {
      removeGoogleMaps();
    };
  }, []);

  const {
    control,
    formState: { errors },
    handleSubmit,
    reset,
    setValue,
  } = useForm({
    mode: "all",
  });

  const ensureSuccess = (resp: any) => {
    if (resp?.data?.status === "ERROR") {
      throw new Error(resp?.data?.message ?? "Greška");
    }
  };

  const refreshCities = async () => {
    const resp = await getAllCities();
    const list: City[] = resp?.data?.result ?? resp?.data ?? [];
    setCitiesApi(list);
  };

  const refreshWarehouses = async () => {
    const resp = await getAllWarehouses();
    const list: Warehouse[] = resp?.data?.result ?? resp?.data ?? [];
    setWarehousesApi(list);
  };

  useEffect(() => {
    Promise.all([refreshCities(), refreshWarehouses()]).catch(() => {
      toast.current?.show({
        severity: "error",
        detail: String(t("Greška pri učitavanju podataka")),
        life: 3000,
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (show) {
      setVisibleNewWarehouseForm(true);
    }
  }, [show]);

  useEffect(() => {
    if (warehouse) {
      setNewWarehouse(warehouse);
      try {
        if (warehouse.latitude && warehouse.longitude) {
          const lat = parseFloat(warehouse.latitude);
          const lng = parseFloat(warehouse.longitude);
          setOverlays([
            new google.maps.Marker({
              position: { lat, lng },
              title: warehouse?.name,
            }),
          ]);
        }
      } catch (ex) {}

      reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [warehouse]);

  const showModal = () => {
    setVisibleNewWarehouseForm(!visibleNewWarehouseForm ? true : false);
  };

  const handleNewWarehouseForm = (prop: string, value: any) => {
    setNewWarehouse((newWarehouse) => ({
      ...newWarehouse,
      [prop]: value,
    }));
  };

  const handleCityChange = (_prop: string, value: any) => {
    let copyOfNewWarehouse = cloneDeep(newWarehouse);
    copyOfNewWarehouse.city = citiesApi.find((item) => item.id == value);
    setNewWarehouse(copyOfNewWarehouse);
  };

  const handleNewWarehouseStatus = () => {
    let copyOfNewWarehouse = cloneDeep(newWarehouse);
    newWarehouse.status === WarehouseStatus.Open
      ? (copyOfNewWarehouse.status = WarehouseStatus.Locked)
      : (copyOfNewWarehouse.status = WarehouseStatus.Open);
    setNewWarehouse(copyOfNewWarehouse);
  };

  const handleNewCity = (prop: string, value: string) => {
    prop === "newCityName"
      ? setNewCity((newCity) => ({ ...newCity, name: value }))
      : setNewCity((newCity) => ({ ...newCity, code: value }));
  };

  const addNewCity = async () => {
    try {
      if (newCity.id) {
        const resp = await updateOneCity(newCity.id, newCity);
        ensureSuccess(resp);
      } else {
        // bitno: ne šaljemo id:""
        const payload: Partial<City> = {
          name: newCity.name,
          code: newCity.code || new Date().getTime().toFixed(),
        };
        const resp = await createCity(payload);
        ensureSuccess(resp);
      }

      setVisibleNewCityAddForm(false);
      setNewCity({ id: "", code: "", name: "" });

      await refreshCities();

      toast.current?.show({
        severity: "success",
        detail: String(t("Mesto je uspešno sačuvano")),
        life: 2500,
      });
    } catch (e: any) {
      toast.current?.show({
        severity: "error",
        detail:
          e?.response?.data?.message ?? String(t("Greška pri čuvanju mesta")),
        life: 3000,
      });
    }
  };

  const hasAnotherVirtualWarehouse = () => {
    const currentVirtual = warehousesApi.find((w) => w.virtual === true);
    if (!currentVirtual) return false;

    if (newWarehouse.id && currentVirtual.id === newWarehouse.id) return false;

    return true;
  };

  const buildUpsertPayload = () => {
    const payload: any = { ...newWarehouse };

    // id ide u URL za update; na create ne šaljemo id:""
    delete payload.id;

    payload.city = newWarehouse.city?.id
      ? { id: newWarehouse.city.id }
      : undefined;

    if (payload.latitude === "") payload.latitude = undefined;
    if (payload.longitude === "") payload.longitude = undefined;

    return payload;
  };

  const onSubmit = async () => {
    try {
      await refreshWarehouses();

      if (newWarehouse.virtual === true && hasAnotherVirtualWarehouse()) {
        toast.current?.show({
          severity: "error",
          detail: newWarehouse.id
            ? String(
                t(
                  "Već je kreiran jedan virtuelni magacin. Ne možete postaviti da još jedan magacin bude virtuelan."
                )
              )
            : String(
                t(
                  "Već je kreiran virtuelni magacin. Ne možete kreirati još jedan."
                )
              ),
          life: 4000,
        });

        if (!newWarehouse.id) {
          handleResetModal();
        }
        return;
      }

      if (newWarehouse.id) {
        const payload = buildUpsertPayload();
        const resp = await updateOneWarehouse(newWarehouse.id, payload);
        ensureSuccess(resp);
      } else {
        const payload = buildUpsertPayload();
        const resp = await createWarehouse(payload);
        ensureSuccess(resp);
      }

      toast.current?.show({
        severity: "success",
        detail: String(t("Uspešno dodati novi podaci")),
        life: 3000,
      });

      refreshingData?.();
      handleResetModal();
    } catch (e: any) {
      toast.current?.show({
        severity: "error",
        detail:
          e?.response?.data?.message ??
          String(t("Greška pri čuvanju magacina")),
        life: 3500,
      });
    }
  };

  const onError = () => {
    //@ts-ignore
    toast.current.show({
      severity: "error",
      detail: t(`Neispravno popunjena polja`),
      life: 3000,
    });
  };

  const handleResetModal = () => {
    setNewWarehouse({
      id: "",
      code: "",
      name: "",
      address: "",
      city: {} as City,
      latitude: undefined,
      longitude: undefined,
      type: "",
      amount: undefined,
      weighbridgeModel: "",
      weighbridgePort: "",
      automated: undefined,
      calculateCalo: undefined,
      virtual: false,
      finishingCenter: false,
    });
    setVisibleNewCityAddForm(false);
    setNewCity({ id: "", code: "", name: "" });
    setOverlays([]);
    showModal();
  };

  const onMapClick = (event: any) => {
    setDialogMapsVisible(true);
    setSelectedPosition(event.latLng);
  };

  const onHideMapsLocation = () => {
    setDialogMapsVisible(false);
  };

  const handleGoogleMapsForm = () => {
    let copyOfNewWarehouse = cloneDeep(newWarehouse);
    copyOfNewWarehouse.latitude = selectedPosition //@ts-ignore
      ? selectedPosition.lat()
      : "";
    copyOfNewWarehouse.longitude = selectedPosition //@ts-ignore
      ? selectedPosition.lng()
      : "";
    setNewWarehouse(copyOfNewWarehouse);
  };

  const addMarker = () => {
    let newMarker = new google.maps.Marker({
      position: {
        //@ts-ignore
        lat: selectedPosition ? selectedPosition.lat() : "", //@ts-ignore
        lng: selectedPosition ? selectedPosition.lng() : "",
      },
      title: newWarehouse.name,
    });
    setOverlays([newMarker]);
  };

  const onOverlayClick = (event: any) => {
    let isMarker = event.overlay.getTitle !== undefined;

    if (isMarker) {
      let title = event.overlay.getTitle(); //@ts-ignore
      infoWindow.current = infoWindow.current || new google.maps.InfoWindow(); //@ts-ignore
      infoWindow.setContent("<div>" + title + "</div>"); //@ts-ignore
      infoWindow.open(event.map, event.overlay);
      event.map.setCenter(event.overlay.getPosition());
    }
  };

  let addButtonStyle: React.CSSProperties = {};
  if (buttonStyle) {
    addButtonStyle = buttonStyle;
  } else {
    addButtonStyle.background = "rgba(0, 165, 83, 1)";
    addButtonStyle.border = "none";
  }

  let addButtonSaveStyle: React.CSSProperties = {};
  if (buttonSaveStyle) {
    addButtonSaveStyle = buttonSaveStyle;
  } else {
    addButtonSaveStyle.background = "rgba(0, 165, 83, 1)";
    addButtonSaveStyle.border = "none";
  }

  let addButtonNewCityStyle: React.CSSProperties = {};
  if (buttonNewCityStyle) {
    addButtonNewCityStyle = buttonNewCityStyle;
  } else {
    addButtonNewCityStyle.background = "rgba(21, 115, 71, 1)";
    addButtonNewCityStyle.border = "none";
  }

  let addButtonInactiveStyle: React.CSSProperties = {};
  if (buttonInactiveStyle) {
    addButtonInactiveStyle = buttonInactiveStyle;
  } else {
    addButtonInactiveStyle.background = "rgba(108, 117, 125, 1)";
    addButtonInactiveStyle.border = "none";
  }

  const canEdit =
    activeUser.role?.name === RoleType.Superadmin ||
    activeUser.role?.name === RoleType.Administrator;

  const header = (
    <div className="header">
      <span className="p-dialog-titlebar-icon p-link">
        <i className="bi bi-shop-window" style={{ fontSize: 18 }}></i>
      </span>
      {newWarehouse.id === "" ? (
        <span>{t("Dodavanje novog magacina")}</span>
      ) : (
        <span>{t("Izmena magacina")}</span>
      )}
    </div>
  );

  const footer = (
    <div className="footer-buttons">
      <ButtonInput
        labelText={t("Sačuvaj")}
        onButtonClick={handleSubmit(onSubmit, onError)}
        addStyle={addButtonSaveStyle}
        disabled={!canEdit}
      />
      <ButtonInput
        labelText={t("Otkaži")}
        onButtonClick={handleResetModal}
        addStyle={addButtonInactiveStyle}
        disabled={!canEdit}
      />
    </div>
  );

  const footerGoogleMaps = (
    <div className="footer-buttons">
      <ButtonInput
        labelText={t("Sačuvaj")}
        onButtonClick={() => {
          addMarker();
          handleGoogleMapsForm();
          onHideMapsLocation();
        }}
        addStyle={addButtonSaveStyle}
        disabled={!canEdit}
      />
      <ButtonInput
        labelText={t("Otkaži")}
        onButtonClick={onHideMapsLocation}
        addStyle={addButtonInactiveStyle}
        disabled={!canEdit}
      />
    </div>
  );

  return (
    <>
      <ButtonInput
        labelText={buttonLabel as string}
        icon={buttonIcon}
        onButtonClick={showModal}
        addStyle={addButtonStyle}
      />
      <Toast ref={toast} position={"top-right"} />
      {visibleNewWarehouseForm ? (
        <div>
          <Dialog
            header={header}
            visible={true}
            style={{ width: "50vw" }}
            className="new-warehouse-modal"
            footer={footer}
            blockScroll={false}
            draggable={false}
            onHide={showModal}
          >
            <div className="new-warehouse-form-body">
              <TextInputWithIcon
                id="name"
                value={newWarehouse.name}
                onValueChange={handleNewWarehouseForm}
                labelText={`${t("Naziv magacina/skladišta")} *`}
                icon="bi bi-shop-window"
                iconAlign="left"
                placeholderText={t("Unesite naziv magacina/skladišta")}
                control={control}
                setValue={setValue}
                errors={errors}
                rules={{ required: true }}
                disabled={!canEdit}
              />
              <TextInputWithIcon
                id="address"
                value={newWarehouse.address as string}
                onValueChange={handleNewWarehouseForm}
                labelText={`${t("Adresa magacina/skladišta")} *`}
                icon="bi bi-signpost"
                iconAlign="left"
                placeholderText={t("Unesite adresu magacina/skladišta")}
                control={control}
                setValue={setValue}
                errors={errors}
                rules={{ required: true }}
                disabled={!canEdit}
              />
              <div className="city-content">
                <div className="city-id-content">
                  <DropdownInputWithIcon
                    id="city"
                    value={newWarehouse.city?.id}
                    onValueChange={handleCityChange}
                    options={citiesApi}
                    optionValue="id"
                    optionLabel="name"
                    icon="bi bi-signpost"
                    filter={true}
                    labelText={`${t("Mesto magacina/skladišta")} *`}
                    placeholderText={t("Izaberite mesto magacina/skladišta")}
                    control={control}
                    setValue={setValue}
                    errors={errors}
                    rules={{ required: true }}
                    disabled={!canEdit}
                  />
                </div>
                <div className="city-add-button">
                  {(activeUser.role?.name === RoleType.Superadmin ||
                    activeUser.role?.name === RoleType.Administrator) && (
                    <ButtonInput
                      labelText={t("Dodaj mesto")}
                      onButtonClick={() => {
                        setVisibleNewCityAddForm(
                          !visibleNewCityAddForm ? true : false
                        );
                      }}
                      icon="bi bi-plus-square"
                      iconAlign="left"
                      addStyle={addButtonNewCityStyle}
                    />
                  )}
                </div>
                {visibleNewCityAddForm && (
                  <div className="new-city-name">
                    <TextInputWithIcon
                      id="newCityName"
                      value={newCity.name}
                      onValueChange={handleNewCity}
                      labelText={t("Mesto magacina/skladišta")}
                      icon="bi bi-signpost"
                      iconAlign="left"
                      placeholderText={t("Unesite mesto magacina/skladišta")}
                      control={control}
                      errors={errors}
                      disabled={!canEdit}
                    />
                  </div>
                )}
                {visibleNewCityAddForm && (
                  <div className="new-city-add-button">
                    <ButtonInput
                      labelText={t("Dodaj")}
                      onButtonClick={addNewCity}
                      addStyle={addButtonNewCityStyle}
                    />
                  </div>
                )}
              </div>
              {newWarehouse.id === "" ? (
                <TextInputWithIcon
                  id="code"
                  value={newWarehouse.code}
                  onValueChange={handleNewWarehouseForm}
                  labelText={`${t("Šifra magacina/skladišta")} *`}
                  icon="bi bi-upc-scan"
                  iconAlign="left"
                  placeholderText={t("Unesite šifru magacina/skladišta")}
                  control={control}
                  setValue={setValue}
                  errors={errors}
                  rules={{ required: true }}
                  disabled={!canEdit}
                />
              ) : (
                !newWarehouse.virtual && (
                  <div className="calculate-calo-field">
                    <RadioButtonInput
                      id="calculateCalo"
                      value={newWarehouse.calculateCalo}
                      radioButtonValue1={t("Da")}
                      radioButtonValue2={t("Ne")}
                      onValueChange={handleNewWarehouseForm}
                      labelText={t("Obračun kala")}
                      control={control}
                      setValue={setValue}
                      errors={errors}
                      disabled={!canEdit}
                    />
                  </div>
                )
              )}
              <div className="calculate-calo-field">
                <RadioButtonInput
                  id="virtual"
                  value={newWarehouse.virtual}
                  radioButtonValue1={t("Da")}
                  radioButtonValue2={t("Ne")}
                  onValueChange={handleNewWarehouseForm}
                  labelText={`${t("Virtuelni magacin")} *`}
                  control={control}
                  setValue={setValue}
                  errors={errors}
                  disabled={!canEdit}
                />
              </div>
              {newWarehouse.id !== "" && !newWarehouse.virtual && (
                <DropdownInputWithIcon
                  id="type"
                  value={newWarehouse.type as string}
                  onValueChange={handleNewWarehouseForm}
                  options={[
                    {
                      label: t("Podni magacin"),
                      value: "Podni magacin",
                    },
                    {
                      label: t("Magacin sa ćelijama"),
                      value: "Magacin sa ćelijama",
                    },
                    {
                      label: t("Privremeni magacin"),
                      value: "Privremeni magacin",
                    },
                  ]}
                  icon="bi bi-shop-window"
                  filter={false}
                  labelText={`${t("Tip magacina")} *`}
                  placeholderText={t("Izaberite tip magacina")}
                  control={control}
                  setValue={setValue}
                  errors={errors}
                  rules={newWarehouse.virtual ? {} : { required: true }}
                  disabled={!canEdit}
                />
              )}
              {newWarehouse.id !== "" && !newWarehouse.virtual && (
                <TextInputWithIcon
                  id="amount"
                  value={newWarehouse.amount as number}
                  onValueChange={handleNewWarehouseForm}
                  labelText={`${t("Kapacitet magacina")} *`}
                  icon="bi bi-shop-window"
                  iconAlign="left"
                  placeholderText={t("Unesite kapacitet magacina")}
                  control={control}
                  setValue={setValue}
                  errors={errors}
                  rules={
                    newWarehouse.virtual
                      ? {}
                      : {
                          required: true,
                          pattern: {
                            value: /^\d+$/,
                            message: "Please enter a number",
                          },
                        }
                  }
                  disabled={!canEdit}
                />
              )}
              {newWarehouse.id !== "" && !newWarehouse.virtual && (
                <DropdownInputWithIcon
                  id="weighbridgeModel"
                  value={newWarehouse.weighbridgeModel as string}
                  onValueChange={handleNewWarehouseForm}
                  options={[
                    {
                      label: t("MX100"),
                      value: "mx100",
                    },
                    {
                      label: t("LD5204"),
                      value: "ld5204",
                    },
                    {
                      label: t("Dini Argeo"),
                      value: "dini-argeo",
                    },
                    {
                      label: t("Diva 11"),
                      value: "diva11",
                    },
                  ]}
                  icon="bi bi-card-text"
                  filter={false}
                  labelText={`${t("Model vage")}`}
                  placeholderText={t("Izaberite model vage")}
                  control={control}
                  setValue={setValue}
                  errors={errors}
                  disabled={!canEdit}
                />
              )}
              {newWarehouse.id !== "" && !newWarehouse.virtual && (
                <RadioButtonInput
                  id="finishingCenter"
                  value={newWarehouse.finishingCenter === true}
                  radioButtonValue1={t("Da")}
                  radioButtonValue2={t("Ne")}
                  onValueChange={handleNewWarehouseForm}
                  labelText={t("Doradni centar")}
                  control={control}
                  setValue={setValue}
                  errors={errors}
                  disabled={!canEdit}
                />
              )}
              {newWarehouse.id !== "" && !newWarehouse.virtual && (
                <RadioButtonInput
                  id="status"
                  value={newWarehouse.status === WarehouseStatus.Open}
                  radioButtonValue1={t("Otključan")}
                  radioButtonValue2={t("Zaključan")}
                  onValueChange={handleNewWarehouseStatus}
                  labelText={t("Status magacina")}
                  control={control}
                  setValue={setValue}
                  errors={errors}
                  disabled={!canEdit}
                />
              )}

              {newWarehouse.id !== "" && !newWarehouse.virtual && (
                <div className="google-maps-row">
                  {googleMapsReady && (
                    <GMap
                      options={{
                        center: {
                          lat: 44.787197,
                          lng: 20.457273,
                        },
                        zoom: 7,
                      }}
                      overlays={overlays}
                      onOverlayClick={onOverlayClick}
                      onMapClick={onMapClick}
                      className="maps"
                    />
                  )}
                </div>
              )}
              {newWarehouse.id !== "" && !newWarehouse.virtual && (
                <div className="google-maps-row">
                  <i className="bi bi-map map-icon"></i>
                  <span className="longitude">
                    Longitude: {newWarehouse.longitude}
                  </span>
                  <span>Latitude: {newWarehouse.latitude}</span>
                </div>
              )}
            </div>
          </Dialog>

          <Dialog
            header="Lokacija magacina"
            visible={dialogMapsVisible}
            modal
            footer={footerGoogleMaps}
            onHide={onHideMapsLocation}
          >
            <TextInputWithIcon
              id="nameOfLocation"
              value={newWarehouse.name}
              onValueChange={handleGoogleMapsForm}
              labelText={t("Naziv magacina/skladišta")}
              icon="bi bi-shop-window"
              iconAlign="left"
              placeholderText=""
              control={control}
              setValue={setValue}
              errors={errors}
              readOnly={true}
            />
            <div className="google-maps-coordinates">
              <TextInputWithIcon
                id="latitude" //@ts-ignore
                value={selectedPosition ? selectedPosition.lat() : ""}
                onValueChange={handleGoogleMapsForm}
                labelText={t("Latitude")}
                icon="bi bi-geo-alt"
                iconAlign="left"
                placeholderText=""
                control={control}
                setValue={setValue}
                errors={errors}
                readOnly={true}
              />
            </div>
            <TextInputWithIcon
              id="latitude" //@ts-ignore
              value={selectedPosition ? selectedPosition.lng() : ""}
              onValueChange={handleGoogleMapsForm}
              labelText={t("Longitude")}
              icon="bi bi-geo-alt"
              iconAlign="left"
              placeholderText=""
              control={control}
              setValue={setValue}
              errors={errors}
              readOnly={true}
            />
          </Dialog>
        </div>
      ) : (
        <></>
      )}
    </>
  );
};
